import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useP } from '../../i18n/useP';
import PageShell from '../../components/PageShell';
import Tabs from '../../components/Tabs';
import Card from '../../components/Card';
import CardShell from '../../components/CardShell';
import SearchField from '../../components/SearchField';
import FilterCount from '../../components/FilterCount';
import FilterIcon from '../../components/FilterIcon';
import PersonTh from '../../components/PersonTh';
import PersonName from '../../components/PersonName';
import { usePersonNameOrder } from '../../hooks/useTableSort';
import SegmentedToggle from '../../components/SegmentedToggle';
import PillFilter from '../../components/PillFilter';
import FilterSearch from '../../components/FilterSearch';
import { useHubExcelExport } from '../../components/ExcelExportButton';
import { useRegisterPageExports } from '../../exports/pageExports';
import { usePersistentValue, usePersistentValueLocal } from '../../hooks/usePersistentState';
import { formatNumber } from '../../utils/format';
import { getChrYearly } from '../../api/chrYearly';
import {
  fetchPersonnelCostCascade,
  computeBurdenRatesByPool,
  readCascadeMode,
} from '../../api/tdabc/personnelCostCascade';
import { listCostPools } from '../../api/tdabc/cost_pools';
import * as rc from '../../api/tdabc/rc';
import './RentabiliteClientPage.css';

/* Rentabilité client — modèle 2 étapes (backend).
   Étape 1 (Saisie · Coût) : heures timbrées personne × prestation → coût = Σ h×CCR.
   Étape 2 (revenu) : par prestation, mode HORAIRE (Σ h × prix) ou FORFAIT (CA saisi).
   Synthèse : marge par prestation + ligne « Interne » (idle = TR − h timbrées, coût
   seul, méthode Kaplan) → résultat net. Bascule LCR / CCR (idle à part) / PCR (idle alloué)
   — le coût par prestation = Σ heures × taux personne (identique à l'Affectation Temps)
   — même résultat net, seule la présentation de l'idle change. */

const parseNum = (v) => {
  if (v === '' || v === null || v === undefined) return 0;
  const n = parseFloat(String(v).replace(/['’\s]/g, '').replace(',', '.'));
  return Number.isFinite(n) ? n : 0;
};
const num = (v, d = 0) => formatNumber(v, d) || '0';
const segOf = (q) => (q.segment || '').trim() || 'Sans segment';
const marginColor = (v) => (!Number.isFinite(v) || Math.abs(v) < 0.5)
  ? 'var(--color-text-muted)' : (v > 0 ? 'var(--color-sage)' : 'var(--color-terracotta)');
const marginTint = (v) => (!Number.isFinite(v) || Math.abs(v) < 0.5) ? undefined
  : (v > 0 ? 'color-mix(in srgb, var(--color-sage) 12%, transparent)'
           : 'color-mix(in srgb, var(--color-terracotta) 12%, transparent)');

const TABS = [
  { id: 'settings', label: 'Liste des services' },
  { id: 'saisie',   label: 'Affectation Temps' },
  { id: 'synthese', label: 'Analyse des marges' },
  { id: 'groupe',   label: 'Marge par groupe' },
];

// Synthèse : coût par service = Σ heures × taux personne (LCR/CCR/PCR), + détail des
// personnes qui composent chaque service (drill-down). Réutilisé en vue complète
// (parent) et en vue filtrée (SyntheseTab) — le périmètre = les persons/services passés.
function buildSynthesis(persons, prestations, hours, costMode) {
  const h = (pid, qid) => Number(hours[pid]?.[qid] || 0);
  const personBillable = {};
  persons.forEach(p => { personBillable[p.id] = prestations.reduce((s, q) => s + h(p.id, q.id), 0); });
  const perRate = costMode === 'lcr' ? 'lcr' : costMode === 'pcr' ? 'pcr' : 'ccr';
  const totalTR = persons.reduce((s, p) => s + p.tr, 0);
  const totalBillable = persons.reduce((s, p) => s + personBillable[p.id], 0);
  const fullCost = persons.reduce((s, p) => s + p.tr * p.ccr, 0);   // Σ TR×CCR (invariant)
  const idleHours = totalTR - totalBillable;

  const rows = prestations.map(q => {
    // Détail : les personnes qui ont travaillé sur ce service.
    const people = persons
      .map(p => { const hh = h(p.id, q.id); return hh > 0
        ? { id: p.id, name: p.name, group: p.group, hours: hh, cost: hh * (p[perRate] || 0), rate: p[perRate] || 0 }
        : null; })
      .filter(Boolean)
      .sort((a, b) => b.hours - a.hours);
    const qHours = people.reduce((s, p) => s + p.hours, 0);
    const cost = people.reduce((s, p) => s + p.cost, 0);
    const revenue = q.revenue_mode === 'flat'
      ? Number(q.flat_revenue || 0)
      : qHours * Number(q.unit_price || 0);
    return { id: q.id, name: q.name, segment: segOf(q), mode: q.revenue_mode,
             hours: qHours, cost, revenue, margin: revenue - cost,
             tarifEff: qHours > 0 ? revenue / qHours : 0,
             costRate: qHours > 0 ? cost / qHours : 0, people };
  });
  const totalRev = rows.reduce((s, r) => s + r.revenue, 0);
  const sumCost = rows.reduce((s, r) => s + r.cost, 0);   // Σ coût services @ mode (table)
  // ── Décomposition INVARIANTE (toujours au CCR, indépendante du mode) ──
  // Coût direct = heures allouées × CCR ; Sous-activité = idle × CCR ; les deux ne
  // changent PAS d'une vue à l'autre. Le mode ne modifie que la lecture par service.
  const directLCR = persons.reduce((s, p) => s + personBillable[p.id] * p.lcr, 0);  // partiel = salaire chargé alloué
  const directCost = persons.reduce((s, p) => s + personBillable[p.id] * p.ccr, 0); // complet @ CCR
  const salaryCost = persons.reduce((s, p) => s + p.tr * p.lcr, 0);   // salaire chargé de TOUTE la capacité (variable)
  const idleCost = Math.max(0, fullCost - directCost);       // sous-activité (invariant, @ CCR)
  // Indirect non affecté = ce qui manque entre le coût direct (CCR) et le coût lu au
  // mode courant. LCR : personnel indirect + charges indirectes + frais de missions
  // pas encore affectés (positif). CCR : ~0. PCR : négatif (idle réparti dans services).
  const indirectUnalloc = directCost - sumCost;
  return { rows, personBillable, totalTR, totalBillable, totalCost: fullCost,
           directLCR, directCost, salaryCost, idleCost, indirectUnalloc, idleHours, totalRev, sumCost,
           netResult: totalRev - fullCost };
}

export default function RentabiliteClientPage({ activeTenant, addToast, year, only = null, title = null }) {
  const { t } = useTranslation();
  const p = useP();
  const tid = activeTenant?.id;
  const [tabMem, setTab] = usePersistentValue(`rc2_tab_${tid}`, 'synthese');
  /* `only` : le hub sert alors UNE section réduite à cet onglet — « Liste des
     services » est passée dans Saisie et réglages le 2026-08-27, c'est un
     référentiel, pas une analyse. */
  const tab = only || ((tabMem === 'settings' || tabMem === 'saisie') ? 'synthese' : tabMem);
  const [costMode, setCostMode] = usePersistentValue(`rc2_costmode_${tid}`, 'ccr'); // 'lcr' | 'ccr' | 'pcr'
  const [unit, setUnit] = usePersistentValue(`rc2_unit_${tid}`, 'chf'); // 'chf' | 'kchf'
  /* L'unité monte à la BANDE DE CONTEXTE, comme dans Finance (2026-09-18) :
     elle tenait la droite de la ligne des onglets, où elle changeait de place
     d'un onglet à l'autre. Le gabarit la place, la page la déclare. */
  const pageUnit = useMemo(() => ({ value: unit, onChange: setUnit }), [unit, setUnit]);
  const [loading, setLoading] = useState(false);

  const [chrRows, setChrRows] = useState([]);
  const [burden, setBurden] = useState({});
  const [overheadPools, setOverheadPools] = useState(() => new Set());
  const [prestations, setPrestations] = useState([]);
  const [hours, setHours] = useState({});   // { personId: { prestationId: number } }

  // ── Base : CCR + TR par personne ───────────────────────────────────────────
  useEffect(() => {
    if (!tid || !year) { setChrRows([]); setBurden({}); return; }
    setLoading(true);
    Promise.all([
      getChrYearly(tid, year),
      fetchPersonnelCostCascade(tid, year),
      listCostPools(tid),
    ])
      .then(([chr, cas, pools]) => {
        setChrRows(chr?.data?.rows || []);
        setBurden(computeBurdenRatesByPool(cas, readCascadeMode(tid)));
        setOverheadPools(new Set((pools?.data || []).filter(p => p.is_overhead_pool).map(p => p.id)));
      })
      .catch(err => addToast?.(err?.message || p('Échec du chargement des données'), 'error'))
      .finally(() => setLoading(false));
  }, [tid, year, addToast]);

  // ── Prestations + heures (backend) ─────────────────────────────────────────
  const reloadRc = useCallback(async () => {
    if (!tid || !year) { setPrestations([]); setHours({}); return; }
    const [pres, hrs] = await Promise.all([rc.listPrestations(tid, year), rc.listRcHours(tid, year)]);
    setPrestations(pres?.data || []);
    const map = {};
    (hrs?.data || []).forEach(h => { (map[h.person_id] = map[h.person_id] || {})[h.prestation_id] = Number(h.hours); });
    setHours(map);
  }, [tid, year]);
  useEffect(() => { reloadRc().catch(e => addToast?.(e?.message || p('Échec du chargement des services'), 'error')); }, [reloadRc, addToast, p]);

  const persons = useMemo(() => chrRows
    .filter(r => !r.cost_pool_id || !overheadPools.has(r.cost_pool_id))
    .map(r => {
      const lcr = Number(r.lcr || 0);
      const ccr = lcr + ((r.cost_pool_id && burden[r.cost_pool_id]) || 0);
      const tr = Number(r.heures_prod || 0);
      const production = Number(r.heures_tf || 0);   // temps de production annuel (TF)
      // Heures NF SAISIES dans « Heures affectées » : la colonne les affiche
      // telles quelles, elle ne les recalcule pas en solde (2026-09-01).
      const nf = Number(r.heures_nf || 0);
      return {
        id: r.person_id,
        name: `${r.first_name || ''} ${r.last_name || ''}`.trim() || '—',
        firstName: r.first_name || '', lastName: r.last_name || '',
        group: r.cost_pool_name || 'Sans groupe', // sentinel FR — translate at display
        lcr,                                          // salaire horaire (CHF/h)
        ccr,                                          // coût horaire de capacité (CHF/h)
        tr,
        production,
        nf,
        pcr: production > 0 ? ccr * tr / production : 0,  // coût de production = CCR×TR/TF
      };
    })
    .sort((a, b) => a.group.localeCompare(b.group, 'fr') || a.name.localeCompare(b.name, 'fr')),
    [chrRows, burden, overheadPools]);

  // ── Handlers prestations ────────────────────────────────────────────────────
  const addPrestation = async () => {
    try { await rc.createPrestation(tid, year, { name: `Service ${prestations.length + 1}`, revenue_mode: 'hourly' }); await reloadRc(); }
    catch (e) { addToast?.(e?.message || p('Échec de création'), 'error'); }
  };
  const patchPrestation = async (id, patch) => {
    setPrestations(prev => prev.map(p => p.id === id ? { ...p, ...patch } : p)); // optimiste
    try { await rc.patchPrestation(tid, year, id, patch); }
    catch (e) { addToast?.(e?.message || p('Échec enregistrement'), 'error'); reloadRc(); }
  };
  const removePrestation = async (id) => {
    try { await rc.deletePrestation(tid, year, id); await reloadRc(); }
    catch (e) { addToast?.(e?.message || p('Échec suppression'), 'error'); }
  };

  // ── Handler heures ──────────────────────────────────────────────────────────
  const setHour = (pid, qid, val) => {
    setHours(prev => ({ ...prev, [pid]: { ...(prev[pid] || {}), [qid]: val } }));
    rc.upsertRcHours(tid, year, { person_id: pid, prestation_id: qid, hours: val })
      .catch(e => addToast?.(e?.message || p('Échec enregistrement heures'), 'error'));
  };

  // ── Calculs (synthèse) — vue complète (SaisieTab en consomme les totaux) ─────
  const calc = useMemo(
    () => buildSynthesis(persons, prestations, hours, costMode),
    [persons, prestations, hours, costMode]);


  // Rendu d'un onglet pour l'export Excel (1 onglet écran = 1 feuille ; unité CHF).
  const renderForExport = (id) => {
    switch (id) {
      case 'settings': return <ParametrageTab prestations={prestations} add={addPrestation} patch={patchPrestation} remove={removePrestation} />;
      case 'saisie':   return <SaisieTab persons={persons} prestations={prestations} hours={hours} setHour={setHour} calc={calc} />;
      case 'synthese': return <SyntheseTab persons={persons} prestations={prestations} hours={hours} costMode={costMode} setCostMode={setCostMode} unit="chf" year={year} />;
      case 'groupe':   return <MargeGroupeTab persons={persons} prestations={prestations} hours={hours} costMode={costMode} setCostMode={setCostMode} unit="chf" year={year} />;
      default: return null;
    }
  };

  // Export Excel seul (pas de PDF ici) → registre, rendu par l'icône
  // télécharger de la barre supérieure.
  const excel = useHubExcelExport({
    tabs: TABS.map(tabDef => ({ label: p(tabDef.label), render: () => renderForExport(tabDef.id) })),
    fileBase: t('phrases:p_cf601e597b'),
    tenantName: activeTenant?.name,
    year,
    addToast,
  });
  useRegisterPageExports(useMemo(() => ([
    { id: 'excel', label: 'Excel', run: excel.run },
  ]), [excel.run]));

  return (
    <PageShell framed kind="report" title={title || t('phrases:p_cf601e597b')}
               unit={(tab === 'synthese' || tab === 'groupe') ? pageUnit : null}>
      {/* « Affectation Temps » a fondé la section « Saisir le temps par
          activité » le 2026-08-30 : c'est une SAISIE, elle n'a rien à faire
          entre deux analyses. Comme « Liste des services », elle quitte la
          barre sans quitter le composant. */}
      {!only && <Tabs tabs={TABS.filter(td => td.id !== 'settings' && td.id !== 'saisie').map(tabDef => ({ ...tabDef, label: p(tabDef.label) }))} active={tab} onChange={setTab}
 />}
      {excel.stage}
      <div className="page-shell__panel">
        {loading && <p className="page-table-loading">{t('common:status.loading')}</p>}
        {!loading && !activeTenant && <div className="page-table-empty">{p('Sélectionner une entreprise.')}</div>}
        {!loading && activeTenant && tab === 'settings' && (
          <ParametrageTab prestations={prestations} add={addPrestation} patch={patchPrestation} remove={removePrestation} />
        )}
        {!loading && activeTenant && tab === 'saisie' && (
          <SaisieTab persons={persons} prestations={prestations} hours={hours} setHour={setHour} calc={calc} />
        )}
        {!loading && activeTenant && tab === 'synthese' && (
          <SyntheseTab persons={persons} prestations={prestations} hours={hours}
                       costMode={costMode} setCostMode={setCostMode} unit={unit} year={year} />
        )}
        {!loading && activeTenant && tab === 'groupe' && (
          <MargeGroupeTab persons={persons} prestations={prestations} hours={hours}
                          costMode={costMode} setCostMode={setCostMode} unit={unit} year={year} />
        )}
      </div>
    </PageShell>
  );
}

/* ── Onglet Liste de services : prestations (nom, segment, mode, prix/CA) ───── */

function ParametrageTab({ prestations, add, patch, remove }) {
  const { t } = useTranslation();
  const p = useP();
  // Segment et Mode RÉDUISENT les lignes : ce sont des filtres, donc toolbar.
  const [segSel, setSegSel] = useState(() => new Set());
  const [modeFilter, setModeFilter] = useState('');
  const [search, setSearch] = useState('');

  const segments = [...new Set(prestations.map(segOf))];
  const needle = search.trim().toLowerCase();
  const visible = prestations.filter(q =>
    (segSel.size === 0 || segSel.has(segOf(q))) &&
    (!modeFilter || q.revenue_mode === modeFilter) &&
    (!needle || `${q.name || ''} ${segOf(q)}`.toLowerCase().includes(needle)));
  const activeFilters = segSel.size + (modeFilter ? 1 : 0) + (needle ? 1 : 0);

  return (
    <CardShell
      title={p('Liste de services')}
      primaryAction={<button className="lk-btn" onClick={add}>{p('+ Service')}</button>}
      filters={(
        <>
          <FilterIcon />
          <PillFilter
            label={p('Segment')} value={segSel} onChange={setSegSel}
            options={segments.map(s => ({ value: s, label: s }))}
          />
          {/* Mode RÉDUIT les lignes : c'est un filtre, il descend de l'entête. */}
          <SegmentedToggle
            compact
            label={p('Mode')} value={modeFilter} onChange={setModeFilter}
            options={[
              { value: '', label: p('Tous') },
              { value: 'hourly', label: p('Horaire') },
              { value: 'flat', label: p('Forfait') },
            ]}
          />
          <FilterCount count={activeFilters}
                       onClear={() => { setSegSel(new Set()); setModeFilter(''); setSearch(''); }} />
        </>
      )}
      search={<SearchField value={search} onChange={setSearch} placeholder={p('Service…')} />}
      filterCount={activeFilters}
      collapsible storageKey="rc2-services"
      flush
    >
      <table className="rc-param">
        <thead>
          <tr>
            <th>{p('Service')}</th>
            <th style={{ width: 130 }}>{p('Mode')}</th>
            <th className="rc-th-num" style={{ width: 130 }}>{p('Prix/h · ou CA')}</th>
            <th>{p('Segment')}</th>
            <th style={{ width: 60 }} />
          </tr>
        </thead>
        <tbody>
          {prestations.length === 0 && (
            <tr><td colSpan={5} className="page-table-empty">{p('Aucun service — ajoutes-en un.')}</td></tr>
          )}
          {prestations.length > 0 && visible.length === 0 && (
            <tr><td colSpan={5} className="page-table-empty">{p('Aucun service ne correspond au filtre.')}</td></tr>
          )}
          {visible.map(q => (
            <tr key={q.id}>
              <td><input className="rc-param-in" value={q.name} onChange={e => patch(q.id, { name: e.target.value })} /></td>
              <td>
                <select className="rc-param-sel" value={q.revenue_mode} onChange={e => patch(q.id, { revenue_mode: e.target.value })}>
                  <option value="hourly">{p('Horaire')}</option>
                  <option value="flat">{p('Forfait (CA)')}</option>
                </select>
              </td>
              <td className="rc-th-num">
                {q.revenue_mode === 'flat' ? (
                  <input className="rc-param-in rc-param-in--num" type="text" inputMode="decimal" placeholder={p('CA total')}
                    value={q.flat_revenue ?? ''} onChange={e => patch(q.id, { flat_revenue: e.target.value === '' ? null : parseNum(e.target.value) })} />
                ) : (
                  <input className="rc-param-in rc-param-in--num" type="text" inputMode="decimal" placeholder="CHF/h"
                    value={q.unit_price ?? ''} onChange={e => patch(q.id, { unit_price: e.target.value === '' ? null : parseNum(e.target.value) })} />
                )}
              </td>
              <td>
                <input className="rc-param-in" list="rc2-segments" placeholder={t('phrases:p_5f984d60ca')}
                  value={q.segment || ''} onChange={e => patch(q.id, { segment: e.target.value })} />
              </td>
              <td><button className="rc-client-del" onClick={() => remove(q.id)} title={p('Supprimer')}>✕</button></td>
            </tr>
          ))}
        </tbody>
      </table>
      <datalist id="rc2-segments">{[...new Set(prestations.map(segOf))].map(s => <option key={s} value={s} />)}</datalist>
    </CardShell>
  );
}

/* ── Onglet Saisie · Coût : personne × prestation (heures) + idle par personne ─ */
function SaisieTab({ persons, prestations, hours, setHour, calc }) {
  const { t } = useTranslation();
  const p = useP();
  const [sortCol, setSortCol] = useState('last_name_sort');
  // Préférence d'affichage (pas une donnée) : la même pour tous les clients.
  const { nameOrder, flipNameOrder } = usePersonNameOrder('tdabc_rentab_name', sortCol, setSortCol);
  const [sortDir, setSortDir] = useState('asc');
  // Filtres : le segment réduit les COLONNES (services) ; groupe et
  // collaborateurs réduisent les LIGNES.
  const [segSel, setSegSel] = useState(() => new Set());
  // UN SEUL champ pour les lignes : groupes et collaborateurs y cohabitent,
  // préfixés pour rester distincts (« grp: » / « per: »).
  const [rowSel, setRowSel] = useState(() => new Set());
  /* Les colonnes de SYNTHÈSE se replient (2026-08-27) : les taux et les
     totaux d'heures se lisent, ne se saisissent pas. Sur un écran de saisie
     large, on veut pouvoir les mettre de côté pour garder les services à
     l'œil. */
  const [showRates, setShowRates] = usePersistentValueLocal('rc2_alloc_rates', false);
  const [showHours, setShowHours] = usePersistentValueLocal('rc2_alloc_hours', true);
  const handleSort = (col, defDir = 'asc') => sortCol === col
    ? setSortDir(d => d === 'asc' ? 'desc' : 'asc')
    : (setSortCol(col), setSortDir(defDir));
  const ind = (col) => (sortCol === col ? (sortDir === 'asc' ? ' ▲' : ' ▼') : '');   // indicateur SUR la colonne triée uniquement

  if (prestations.length === 0) {
    return <Card flush><div className="rc-empty">{p("Définis d'abord des services dans ")}<b>{p('Liste des services')}</b>.</div></Card>;
  }
  const segments = [...new Set(prestations.map(segOf))];
  const visiblePrestations = segSel.size === 0
    ? prestations
    : prestations.filter(q => segSel.has(segOf(q)));
  const groups = [...new Set(persons.map(pp => pp.group || 'Sans groupe'))];
  // Valeur de tri par colonne (nom/prénom, groupe = texte ; taux/heures/service/alloué = nombre).
  const sortVal = (p, key) => {
    if (key === 'first_name_sort') return (p.firstName || '').toLowerCase();
    if (key === 'last_name_sort')  return (p.lastName || '').toLowerCase();
    if (key === 'group')           return (p.group || '').toLowerCase();
    if (key === 'bill')            return calc.personBillable[p.id] || 0;
    if (key === 'nf')              return p.nf || 0;
    // Contrôle = ce qui reste des heures TF une fois l'affectation faite.
    if (key === 'ctrl')            return (p.production || 0) - (calc.personBillable[p.id] || 0);
    if (key.startsWith('presta:')) return Number(hours[p.id]?.[key.slice(7)] || 0);
    return Number(p[key] || 0);   // lcr / ccr / pcr / tr / production
  };
  const display = [...persons].sort((a, b) => {
    const va = sortVal(a, sortCol), vb = sortVal(b, sortCol);
    const c = (typeof va === 'string' || typeof vb === 'string')
      ? String(va).localeCompare(String(vb), 'fr', { numeric: true, sensitivity: 'base' })
      : (Number(va) - Number(vb));
    return sortDir === 'asc' ? c : -c;
  }).filter(pp => rowSel.size === 0
    || rowSel.has(`per:${pp.id}`)
    || rowSel.has(`grp:${pp.group || 'Sans groupe'}`));
  const h = (pid, qid) => hours[pid]?.[qid] ?? '';

  // Totaux du pied : sur les personnes AFFICHÉES (le filtre lignes se lit).
  const totTR   = display.reduce((s, pp) => s + (pp.tr || 0), 0);
  const totProd = display.reduce((s, pp) => s + (pp.production || 0), 0);
  const totBill = display.reduce((s, pp) => s + (calc.personBillable[pp.id] || 0), 0);
  const totNf   = display.reduce((s, pp) => s + (pp.nf || 0), 0);
  // Coût d'un service (prestation) à un taux horaire donné = Σ heures × taux personne.
  const prestaCostAt = (key, qid) => display.reduce((s, p) => s + Number(hours[p.id]?.[qid] || 0) * (p[key] || 0), 0);
  const totalAllocated = totBill;   // Σ heures allouées affichées (base du coût moyen horaire)
  const RATE_ROWS = [
    { key: 'lcr', label: t('phrases:p_bc7392667f') },
    { key: 'ccr', label: t('phrases:p_7f7997832f') },
    { key: 'pcr', label: t('phrases:p_41d13f4261') },
  ];

  return (
    <CardShell
      flush
      title={p('Allocation du temps')}
      viewControls={(
        /* Les taux se montrent ou se masquent depuis l'entête : chercher un
           bouton au milieu des en-têtes de colonnes n'allait pas de soi. */
        <button type="button" className={`lk-btn lk-btn--toggle${showRates ? ' is-on' : ''}`}
                aria-pressed={showRates} onClick={() => setShowRates(v => !v)}
                title={showRates ? p('Masquer les taux horaires') : p('Afficher les taux horaires')}>
          {showRates ? (
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"
                 strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7z" /><circle cx="12" cy="12" r="3" />
            </svg>
          ) : (
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"
                 strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M3 3l18 18" />
              <path d="M10.6 5.1A10.9 10.9 0 0 1 12 5c6.5 0 10 7 10 7a18 18 0 0 1-3.2 4.1" />
              <path d="M6.2 6.7A18 18 0 0 0 2 12s3.5 7 10 7a10.7 10.7 0 0 0 4.3-.9" />
            </svg>
          )}
          {p('Taux')}
        </button>
      )}
      filters={(
        <>
          <FilterIcon />
          {/* Le segment réduit les COLONNES de services affichées. */}
          <PillFilter
            label={p('Segment')} value={segSel} onChange={setSegSel}
            options={segments.map(s => ({ value: s, label: s }))}
          />
          <FilterCount count={segSel.size + rowSel.size}
                       onClear={() => { setSegSel(new Set()); setRowSel(new Set()); }} />
        </>
      )}
      search={(
        /* Un seul champ pour les LIGNES : groupes et collaborateurs y
           cohabitent, rangés par nature. Dernier élément à droite. */
        <FilterSearch
          items={[
            ...groups.map(g => ({ id: `grp:${g}`, kind: p('Groupe'),
                                  label: g === 'Sans groupe' ? p('Sans groupe') : g })),
            ...persons.map(pp => ({ id: `per:${pp.id}`, kind: p('Collaborateur'), label: pp.name })),
          ]}
          selected={rowSel} onChange={setRowSel}
          placeholder={p('Groupe ou collaborateur…')}
        />
      )}
      filterCount={segSel.size + rowSel.size}
      collapsible storageKey="rc2-allocation"
    >
      <div className="rc-scroll">
        <table className="rc-grid">
          <thead>
            {/* Trois familles de colonnes : ce qu'on LIT (taux, totaux d'heures)
                et ce qu'on SAISIT (les services). Les deux premières se
                replient d'un clic sur leur en-tête. */}
            <tr className="rc-grouphead">
              <th className="rc-gh-sticky" colSpan={2} />
              {showRates && (
                <th className="rc-gh rc-sep-left" colSpan={3}>
                  <span className="rc-gh__in">{p('Taux horaires')}</span>
                </th>
              )}
              {/* Σ Heures couvre AUSSI le Contrôle (2026-09-01) : la colonne
                  flottait entre les deux bandeaux. */}
              <th className="rc-gh rc-gh--click rc-sep-left" colSpan={showHours ? 5 : 3}
                  role="button" tabIndex={0} aria-expanded={showHours}
                  title={showHours ? p('Masquer les heures') : p('Afficher les heures')}
                  onClick={() => setShowHours(v => !v)}
                  onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setShowHours(v => !v); } }}>
                <span className="rc-gh__in">
                  <span className="rc-gh__tog" aria-hidden>{showHours ? '−' : '+'}</span>
                  <span aria-hidden>Σ</span> {p('Heures')}
                </span>
              </th>
              <th className="rc-gh rc-gh--entry rc-sep-strong" colSpan={Math.max(1, visiblePrestations.length)}>
                <span className="rc-gh__in">{p('Heures affectées aux services')}</span>
              </th>
            </tr>
            <tr>
              <PersonTh
                className="rc-sticky rc-th-res"
                sortCol={sortCol} sortDir={sortDir} onSort={handleSort}
                nameOrder={nameOrder} onFlip={flipNameOrder}
              />
              <th className="rc-th-grp rc-th-sortable rc-sticky2" onClick={() => handleSort('group', 'asc')}>{p('Groupe')}{ind('group')}</th>
              {showRates && <>
                <th className="rc-th-num rc-th-sortable rc-sep-left" title={p('Salaire horaire chargé')}
                    onClick={() => handleSort('lcr', 'desc')}>LCR{ind('lcr')}<br /><span className="rc-th-sub">CHF/h</span></th>
                <th className="rc-th-num rc-th-sortable" title={p('Coût horaire de capacité')}
                    onClick={() => handleSort('ccr', 'desc')}>CCR{ind('ccr')}<br /><span className="rc-th-sub">CHF/h</span></th>
                <th className="rc-th-num rc-th-sortable" title={p('Coût de production')}
                    onClick={() => handleSort('pcr', 'desc')}>PCR{ind('pcr')}<br /><span className="rc-th-sub">CHF/h</span></th>
              </>}
              {showHours && <>
                <th className="rc-th-num rc-th-sortable rc-sep-left" title={p('Capacité pratique (TR)')}
                    onClick={() => handleSort('tr', 'desc')}>{p('Capacité')}{ind('tr')}<br /><span className="rc-th-sub">h</span></th>
                <th className="rc-th-num rc-th-sortable" title={p('Temps de production (TF)')}
                    onClick={() => handleSort('production', 'desc')}>{p('Heures TF')}{ind('production')}<br /><span className="rc-th-sub">h</span></th>
              </>}
              {/* Alloué et Écart restent même la famille repliée : c'est le
                  total de la ligne, et ce qu'il en manque. */}
              <th className={`rc-th-num rc-th-sortable${showHours ? '' : ' rc-sep-left'}`}
                  title={p('Heures affectées aux services')}
                  onClick={() => handleSort('bill', 'desc')}>{p('Alloué')}{ind('bill')}<br /><span className="rc-th-sub">h</span></th>
              {/* Les heures NF viennent de la SAISIE annuelle (« Heures
                  affectées »), elles ne se déduisent plus d'un solde ; le
                  contrôle dit ce qu'il reste à affecter du temps facturable
                  (2026-09-01). */}
              <th className="rc-th-num rc-th-sortable" title={p('Heures non facturables saisies dans « Heures affectées »')}
                  onClick={() => handleSort('nf', 'desc')}>{p('Heures NF')}{ind('nf')}<br /><span className="rc-th-sub">h</span></th>
              <th className="rc-th-num rc-th-sortable" title={p('Heures TF moins heures allouées : zéro quand toute la production est ventilée')}
                  onClick={() => handleSort('ctrl', 'asc')}>{p('Contrôle')}{ind('ctrl')}<br /><span className="rc-th-sub">h</span></th>
              {visiblePrestations.map((q, i) => (
                <th key={q.id} className={`rc-th-num rc-th-prod rc-th-sortable${i === 0 ? ' rc-sep-strong' : ''}`}
                    title={q.name}
                    onClick={() => handleSort('presta:' + q.id, 'desc')}>
                  <span className="rc-th-prod__lbl">{q.name}</span>{ind('presta:' + q.id)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {display.map(person => {
              const bill = calc.personBillable[person.id] || 0;
              // Alloué > temps production → sur-affectation (alerte discrète).
              const over = bill - (person.production || 0) > 0.5;
              const groupLabel = person.group === 'Sans groupe' ? p('Sans groupe') : person.group;
              return (
                <tr key={person.id}>
                  <td className="rc-sticky rc-res"><span className="rc-res-name">
                    <PersonName firstName={person.firstName} lastName={person.lastName} order={nameOrder} />
                  </span></td>
                  <td className="rc-grp rc-sticky2">{groupLabel}</td>
                  {showRates && <>
                    <td className="rc-num rc-sep-left" style={{ color: 'var(--color-text-muted)' }}>{num(person.lcr, 2)}</td>
                    <td className="rc-num" style={{ color: 'var(--color-text-muted)' }}>{num(person.ccr, 2)}</td>
                    <td className="rc-num" style={{ color: 'var(--color-text-muted)' }}>{num(person.pcr, 2)}</td>
                  </>}
                  {showHours && <>
                    <td className="rc-num rc-tr rc-sep-left">{num(person.tr)}</td>
                    <td className="rc-num rc-tr">{num(person.production)}</td>
                  </>}
                  <td className={`rc-num${showHours ? '' : ' rc-sep-left'}`}
                      style={over ? { color: 'var(--color-terracotta)', fontWeight: 700 } : undefined}>{num(bill)}</td>
                  {(() => {
                    /* Contrôle : reste de TF à ventiler. Non nul, il signale
                       une saisie incomplète (positif) ou une sur-affectation
                       (négatif) — les deux méritent l'œil. */
                    const ctrl = (person.production || 0) - bill;
                    return (
                      <>
                      <td className="rc-num" style={{ color: 'var(--color-text-muted)' }}>{num(person.nf)}</td>
                      <td className="rc-num" style={Math.abs(ctrl) > 0.5
                        ? { color: 'var(--color-terracotta)', fontWeight: 700 }
                        : { color: 'var(--color-text-muted)' }}>{num(ctrl)}</td>
                      </>
                    );
                  })()}
                  {visiblePrestations.map((q, i) => (
                    <td key={q.id} className={`rc-cell${i === 0 ? ' rc-sep-strong' : ''}`}>
                      <input className="rc-in" type="text" inputMode="decimal"
                        title={`${q.name} · ${p('heures')}`}
                        value={h(person.id, q.id)}
                        onChange={e => setHour(person.id, q.id, parseNum(e.target.value))} />
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
          <tfoot>
            <tr className="rc-foot">
              <td className="rc-sticky rc-res">{p('Total')}</td>
              <td className="rc-sticky2" />
              {showRates && <><td className="rc-sep-left" /><td /><td /></>}
              {showHours && <>
                <td className="rc-num rc-sep-left">{num(totTR)}</td>
                <td className="rc-num">{num(totProd)}</td>
              </>}
              <td className={`rc-num${showHours ? '' : ' rc-sep-left'}`}>{num(totBill)}</td>
              <td className="rc-num">{num(totNf)}</td>
              <td className="rc-num">{num(totProd - totBill)}</td>
              {visiblePrestations.map((q, i) => {
                const qh = display.reduce((s, p) => s + Number(hours[p.id]?.[q.id] || 0), 0);
                return <td key={q.id} className={`rc-num${i === 0 ? ' rc-sep-strong' : ''}`}>{num(qh)}</td>;
              })}
            </tr>
            {/* Coût du produit à chaque taux (LCR/CCR/PCR) + coût moyen horaire (dans la colonne du taux). */}
            {RATE_ROWS.map(rr => {
              const perQ = visiblePrestations.map(q => prestaCostAt(rr.key, q.id));
              const totalCost = perQ.reduce((s, v) => s + v, 0);
              const avg = totalAllocated > 0 ? totalCost / totalAllocated : 0;
              return (
                <tr key={rr.key} className="rc-foot rc-foot-rate">
                  <td className="rc-sticky rc-res">
                    {rr.label}{!showRates && <span className="rc-foot-avg"> · {num(avg, 2)}</span>}
                  </td>
                  <td className="rc-sticky2" />
                  {showRates && <>
                    <td className="rc-num rc-sep-left">{rr.key === 'lcr' ? num(avg, 2) : ''}</td>
                    <td className="rc-num">{rr.key === 'ccr' ? num(avg, 2) : ''}</td>
                    <td className="rc-num">{rr.key === 'pcr' ? num(avg, 2) : ''}</td>
                  </>}
                  {showHours && <><td className="rc-sep-left" /><td /></>}
                  <td className={`rc-num${showHours ? '' : ' rc-sep-left'}`}>{num(totalCost)}</td>
                  {/* Heures NF, puis Contrôle : deux colonnes vides, mais elles
                      DOIVENT être posées. Sans la seconde, toute la série des
                      coûts par service glissait d'une colonne vers la gauche et
                      le dernier service tombait hors du tableau (02.09.2026). */}
                  <td />
                  <td />
                  {perQ.map((v, i) => <td key={i} className={`rc-num${i === 0 ? ' rc-sep-strong' : ''}`}>{num(v)}</td>)}
                </tr>
              );
            })}
          </tfoot>
        </table>
      </div>
    </CardShell>
  );
}

/* ── Onglet Synthèse : marge par service + drill personnes + filtre + Interne ─── */
function SyntheseTab({ persons, prestations, hours, costMode, setCostMode, unit, year }) {
  // Formateur MONTANTS : CHF (entier) ou kCHF (÷1000). Les heures, taux (CHF/h) et % restent inchangés.
  const { t } = useTranslation();
  const p = useP();
  const m = (v) => unit === 'kchf' ? num((v || 0) / 1000, 0) : num(v, 0);
  const [filter, setFilter] = useState('');
  const [segSel, setSegSel] = useState(() => new Set());
  const [expanded, setExpanded] = useState(() => new Set());
  const toggle = (id) => setExpanded(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });

  // Filtre multi-critères : le texte restreint l'axe PERSONNES (nom / groupe) et/ou
  // l'axe SERVICES (nom / segment) selon ce qu'il matche, puis on recalcule dessus.
  const calc = useMemo(() => {
    // Le segment restreint d'abord les services, la recherche fait le reste.
    const prest = segSel.size === 0 ? prestations : prestations.filter(q2 => segSel.has(segOf(q2)));
    const q = filter.trim().toLowerCase();
    if (!q) return buildSynthesis(persons, prest, hours, costMode);
    const matchP = p => `${p.name} ${p.group}`.toLowerCase().includes(q);
    const matchS = s => `${s.name} ${segOf(s)}`.toLowerCase().includes(q);
    const personAxis = persons.some(matchP);
    const serviceAxis = prest.some(matchS);
    if (!personAxis && !serviceAxis) return { rows: [], noMatch: true, totalBillable: 0, totalRev: 0, sumCost: 0, directLCR: 0, directCost: 0, salaryCost: 0, idleCost: 0, indirectUnalloc: 0, idleHours: 0, totalCost: 0, netResult: 0 };
    return buildSynthesis(
      personAxis ? persons.filter(matchP) : persons,
      serviceAxis ? prest.filter(matchS) : prest,
      hours, costMode);
  }, [persons, prestations, hours, costMode, filter, segSel]);

  // Regroupement par segment (sous-totaux)
  const bySeg = {};
  (calc.rows || []).forEach(r => { (bySeg[r.segment] = bySeg[r.segment] || []).push(r); });
  const segments = Object.keys(bySeg);

  /* La BASE change la base de calcul sans réduire les lignes : entête. */
  const baseToggle = (
    <SegmentedToggle
      label={p('Base taux horaire')} value={costMode} onChange={setCostMode}
      ariaLabel={t('phrases:p_c1206b5197')}
      options={[
        { value: 'lcr', label: 'LCR' },
        { value: 'ccr', label: 'CCR' },
        { value: 'pcr', label: 'PCR' },
      ]}
    />
  );
  const allSegments = [...new Set(prestations.map(segOf))];
  const activeFilters = segSel.size + (filter.trim() ? 1 : 0);
  /* Tout déplier / tout replier : ouvrir vingt services un par un pour lire
     qui les produit n'est pas une lecture, c'est une corvée (2026-08-27). */
  const rowIds = (calc.rows || []).map(r => r.id);
  const allOpen = rowIds.length > 0 && rowIds.every(id => expanded.has(id));
  const toggleAll = () => setExpanded(allOpen ? new Set() : new Set(rowIds));
  const SUBTITLE = {
    lcr: p('LCR — analyse contributive : variable = salaire chargé, fixe = indirect → seuil de rentabilité'),
    ccr: p('CCR — coût direct à la capacité (indirect inclus) ; ne reste que la sous-activité'),
    pcr: p('PCR — coût de production, sous-activité répartie dans le coût des services'),
  };

  return (
    <CardShell flush
      title={<>{p('Synthèse par service')} <small className="rc-count">· {year}</small></>}
      subtitle={SUBTITLE[costMode]}
      viewControls={(
        <>
          <button type="button" className="lk-btn" onClick={toggleAll} disabled={rowIds.length === 0}>
            {allOpen ? p('Tout replier') : p('Tout déplier')}
          </button>
          {baseToggle}
        </>
      )}
      filters={(
        <>
          <FilterIcon />
          <PillFilter
            label={p('Segment')} value={segSel} onChange={setSegSel}
            options={allSegments.map(sg => ({ value: sg, label: sg }))}
          />
          <FilterCount count={activeFilters}
                       onClear={() => { setSegSel(new Set()); setFilter(''); }} />
        </>
      )}
      search={(
        <SearchField value={filter} onChange={setFilter}
                     placeholder={p('Service, segment, personne…')} />
      )}
      filterCount={activeFilters}
      collapsible storageKey="rc2-analyse"
    >
      {/* Tuiles par vue : chaque mode met en avant une marge différente.
         LCR = analyse contributive + seuil de rentabilité (variable = salaire, fixe = indirect). */}
      {(() => {
        const rev = calc.totalRev || 0;
        const pctStr = (x) => rev > 0 ? `${num(x / rev * 100, 0)}%` : '—';
        // CVP : SEUL coût variable = coût direct des heures prestées (LCR alloué).
        // Tout le reste — sous-activité (idle) ET indirect — est FIXE (effectif salarié).
        const margeCV      = rev - (calc.directLCR || 0);                   // marge sur coûts variables
        const chargesFixes = (calc.totalCost || 0) - (calc.directLCR || 0); // idle + indirect = fixe
        const margeBrute   = rev - (calc.directCost || 0);                  // CCR : rev − coût complet alloué
        // Seuil de rentabilité = charges fixes ÷ taux de marge sur coûts variables.
        const tauxCV = rev > 0 ? margeCV / rev : 0;
        const seuil = tauxCV > 0 ? chargesFixes / tauxCV : null;
        return (
          <div className="rc-kpis">
            {costMode === 'lcr' && <>
              <Kpi label={p('Revenus')} value={m(rev)} />
              <Kpi label={t('phrases:p_eec2fc7233')} value={m(calc.directLCR)} color="var(--color-text-muted)" />
              <Kpi label={t('phrases:p_86ff859120')} value={m(margeCV)} sub={pctStr(margeCV)} color={marginColor(margeCV)} strong />
              <Kpi label={p('Charges fixes')} value={m(chargesFixes)} color="var(--color-text-muted)" />
              <Kpi label={t('phrases:p_1bbe6f47b1')} value={m(calc.netResult)} sub={pctStr(calc.netResult)} color={marginColor(calc.netResult)} strong />
              <Kpi label={t('phrases:p_5d25ea2d1d')} value={seuil != null ? m(seuil) : '—'} sub={seuil != null ? pctStr(seuil) + ' ' + p('du CA') : ''} />
            </>}
            {costMode === 'ccr' && <>
              <Kpi label={p('Revenus')} value={m(rev)} />
              <Kpi label={t('phrases:p_fa61fcd69e')} value={m(calc.directCost)} color="var(--color-text-muted)" />
              <Kpi label={p('Marge brute')} value={m(margeBrute)} sub={pctStr(margeBrute)} color={marginColor(margeBrute)} />
              <Kpi label={t('phrases:p_1dce073b94')} value={m(calc.idleCost)} color="var(--color-text-muted)" />
              <Kpi label={p('Perte & profit')} value={m(calc.netResult)} sub={pctStr(calc.netResult)} color={marginColor(calc.netResult)} strong />
            </>}
            {costMode === 'pcr' && <>
              <Kpi label={p('Revenus')} value={m(rev)} />
              <Kpi label={t('phrases:p_0dbe2ab6ca')} value={m(calc.totalCost)} color="var(--color-text-muted)" />
              <Kpi label={p('Perte & profit')} value={m(calc.netResult)} sub={pctStr(calc.netResult)} color={marginColor(calc.netResult)} strong />
            </>}
          </div>
        );
      })()}
      <div className="rc-scroll">
        <table className="rc-table">
          <thead>
            <tr>
              <th className="rc-th-res">{p('Service')}</th>
              <th className="rc-th-num">{p('Heures')}</th>
              <th className="rc-th-num">Mix%</th>
              <th className="rc-th-num">{p('Tarif')}</th>
              <th className="rc-th-num">{p('Coût h. moyen')}</th>
              <th className="rc-th-num">{p('Revenus')}</th>
              <th className="rc-th-num">{p('Coût')}</th>
              <th className="rc-th-num rc-col-res">{costMode === 'lcr' ? p('Marge directe') : costMode === 'pcr' ? p('Perte & Profit') : p('Marge brute')}</th>
              <th className="rc-th-num">%</th>
            </tr>
          </thead>
          <tbody>
            {(calc.rows || []).length === 0 && <tr><td colSpan={9} className="page-table-empty">{calc.noMatch ? p('Aucun résultat pour ce filtre.') : p('Aucun service.')}</td></tr>}
            {segments.map(seg => {
              const list = bySeg[seg];
              const sRev = list.reduce((s, r) => s + r.revenue, 0);
              const sCost = list.reduce((s, r) => s + r.cost, 0);
              const multi = segments.length > 1;
              return (
                <React.Fragment key={seg}>
                  {list.map(r => (
                    <React.Fragment key={r.id}>
                    <tr>
                      {/* Toute la cellule ouvre le service : viser un chevron de
                          8 px n'était pas une cible raisonnable (2026-08-27). */}
                      <td className="rc-res rc-res--click" role="button" tabIndex={0}
                          aria-expanded={expanded.has(r.id)}
                          title={p('Voir les personnes')}
                          onClick={() => toggle(r.id)}
                          onKeyDown={e => {
                            if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggle(r.id); }
                          }}>
                        <span className={`rc-drill-toggle${expanded.has(r.id) ? ' is-open' : ''}`} aria-hidden>
                          {expanded.has(r.id) ? '▾' : '▸'}
                        </span>
                        <span className="rc-res-name" style={{ display: 'inline' }}>{r.name}</span>
                      </td>
                      <td className="rc-num">{num(r.hours)}</td>
                      <td className="rc-num" style={{ color: 'var(--color-text-muted)' }}>{calc.totalBillable > 0 ? num(r.hours / calc.totalBillable * 100, 0) + '%' : '—'}</td>
                      <td className="rc-num">{r.hours > 0 ? num(r.tarifEff, 0) : (r.mode === 'flat' ? p('forfait') : '—')}</td>
                      <td className="rc-num" style={{ color: 'var(--color-text-muted)' }}>{r.hours > 0 ? num(r.costRate, 0) : '—'}</td>
                      <td className="rc-num">{m(r.revenue)}</td>
                      <td className="rc-num" style={{ color: 'var(--color-text-muted)' }}>{m(r.cost)}</td>
                      <td className="rc-num rc-col-res" style={{ color: marginColor(r.margin), fontWeight: 700, background: marginTint(r.margin) }}>{m(r.margin)}</td>
                      <td className="rc-num" style={{ color: marginColor(r.margin) }}>{r.revenue > 0 ? num(r.margin / r.revenue * 100, 0) + '%' : '—'}</td>
                    </tr>
                    {expanded.has(r.id) && r.people.map(pp => {
                      // Revenu de la personne = sa part du revenu du service (pro-rata heures) ; marge = revenu − coût.
                      const revPP = r.hours > 0 ? pp.hours / r.hours * r.revenue : 0;
                      const margPP = revPP - pp.cost;
                      return (
                      <tr key={r.id + '-' + pp.id} className="rc-drill-row">
                        <td className="rc-res rc-drill-cell"><span className="rc-drill-name">{pp.name}</span><span className="rc-drill-grp">{pp.group}</span></td>
                        <td className="rc-num">{num(pp.hours)}</td>
                        <td className="rc-num" style={{ color: 'var(--color-text-muted)' }}>{r.hours > 0 ? num(pp.hours / r.hours * 100, 0) + '%' : '—'}</td>
                        <td className="rc-num" style={{ color: 'var(--color-text-muted)' }}>{r.tarifEff > 0 ? num(r.tarifEff, 0) : '—'}</td>
                        <td className="rc-num" style={{ color: 'var(--color-text-muted)' }}>{num(pp.rate, 0)}</td>
                        <td className="rc-num">{m(revPP)}</td>
                        <td className="rc-num" style={{ color: 'var(--color-text-muted)' }}>{m(pp.cost)}</td>
                        <td className="rc-num rc-col-res" style={{ color: marginColor(margPP), fontWeight: 700 }}>{m(margPP)}</td>
                        <td className="rc-num" style={{ color: marginColor(margPP) }}>{revPP > 0 ? num(margPP / revPP * 100, 0) + '%' : '—'}</td>
                      </tr>
                      );
                    })}
                    {expanded.has(r.id) && r.people.length === 0 && (
                      <tr className="rc-drill-row"><td className="rc-res rc-drill-cell" colSpan={9} style={{ color: 'var(--color-text-muted)' }}>{p('Aucune personne affectée à ce service.')}</td></tr>
                    )}
                    </React.Fragment>
                  ))}
                  {multi && (
                    <tr className="rc-subtotal">
                      <td className="rc-res">Σ {seg}</td>
                      <td className="rc-num">{num(list.reduce((s, r) => s + r.hours, 0))}</td>
                      <td className="rc-num">{(() => { const sh = list.reduce((s, r) => s + r.hours, 0); return calc.totalBillable > 0 ? num(sh / calc.totalBillable * 100, 0) + '%' : '—'; })()}</td>
                      <td />
                      <td className="rc-num" style={{ color: 'var(--color-text-muted)' }}>{(() => { const sh = list.reduce((s, r) => s + r.hours, 0); return sh > 0 ? num(sCost / sh, 0) : '—'; })()}</td>
                      <td className="rc-num">{m(sRev)}</td><td className="rc-num">{m(sCost)}</td>
                      <td className="rc-num rc-col-res" style={{ color: marginColor(sRev - sCost), fontWeight: 700 }}>{m(sRev - sCost)}</td>
                      <td className="rc-num">{sRev > 0 ? num((sRev - sCost) / sRev * 100, 0) + '%' : '—'}</td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
            {/* Indirect non affecté : écart entre le coût direct (CCR) et le coût lu au
                mode courant. LCR = personnel indirect + charges + frais non affectés. */}
            {calc.indirectUnalloc > 0.5 && (
              <tr className="rc-interne">
                <td className="rc-res">{p('Indirect non affecté')} <span className="rc-res-tr">{p('personnel indirect · charges · frais')}</span></td>
                <td className="rc-num">—</td><td className="rc-num">—</td><td className="rc-num">—</td><td className="rc-num">—</td><td className="rc-num">—</td>
                <td className="rc-num" style={{ color: 'var(--color-text-muted)' }}>{m(calc.indirectUnalloc)}</td>
                <td className="rc-num rc-col-res" style={{ color: 'var(--color-terracotta)', fontWeight: 700 }}>{m(-calc.indirectUnalloc)}</td>
                <td className="rc-num">—</td>
              </tr>
            )}
            {/* Sous-activité (idle × CCR) — montrée en LCR/CCR (idle à part) ; en PCR
                l'idle est réparti dans les services (pas de ligne). La TUILE reste constante. */}
            {costMode !== 'pcr' && calc.idleCost > 0.5 && (
              <tr className="rc-interne">
                <td className="rc-res">{p('Sous-activité')} <span className="rc-res-tr">{p('capacité non utilisée')}</span></td>
                <td className="rc-num">{num(calc.idleHours)}</td>
                <td className="rc-num">—</td>
                <td className="rc-num">—</td>
                <td className="rc-num" style={{ color: 'var(--color-text-muted)' }}>{calc.idleHours > 0 ? num(calc.idleCost / calc.idleHours, 0) : '—'}</td>
                <td className="rc-num">—</td>
                <td className="rc-num" style={{ color: 'var(--color-text-muted)' }}>{m(calc.idleCost)}</td>
                <td className="rc-num rc-col-res" style={{ color: 'var(--color-terracotta)', fontWeight: 700 }}>{m(-calc.idleCost)}</td>
                <td className="rc-num">—</td>
              </tr>
            )}
          </tbody>
          <tfoot>
            <tr className="rc-foot">
              <td className="rc-res">{p('RÉSULTAT NET')}</td>
              <td className="rc-num">{num((calc.totalBillable || 0) + (costMode !== 'pcr' ? (calc.idleHours || 0) : 0))}</td>
              <td className="rc-num">100%</td>
              <td />
              <td className="rc-num">{(() => { const th = (calc.totalBillable || 0) + (costMode !== 'pcr' ? (calc.idleHours || 0) : 0); return th > 0 ? num(calc.totalCost / th, 0) : '—'; })()}</td>
              <td className="rc-num">{m(calc.totalRev)}</td>
              <td className="rc-num">{m(calc.totalCost)}</td>
              <td className="rc-num rc-col-res" style={{ color: marginColor(calc.netResult) }}>{m(calc.netResult)}</td>
              <td className="rc-num">{calc.totalRev > 0 ? num(calc.netResult / calc.totalRev * 100, 0) + '%' : '—'}</td>
            </tr>
          </tfoot>
        </table>
      </div>
    </CardShell>
  );
}

/* ── Onglet Marge par groupe : services (lignes, sous-totaux segment) × groupes ─
   (colonnes). Cellule = marge du groupe sur le service (revenu pro-rata − coût @ mode). */
function MargeGroupeTab({ persons, prestations, hours, costMode, setCostMode, unit, year }) {
  const { t } = useTranslation();
  const p = useP();
  const m = (v) => unit === 'kchf' ? num((v || 0) / 1000, 0) : num(v, 0);
  const calc = useMemo(() => buildSynthesis(persons, prestations, hours, costMode),
    [persons, prestations, hours, costMode]);
  const groups = useMemo(
    () => [...new Set(persons.map(p => p.group))].sort((a, b) => a.localeCompare(b, 'fr')),
    [persons]);

  // Marge par (service × groupe) : agrège le détail par personne de chaque service.
  const svcRows = (calc.rows || []).map(r => {
    const byGroup = {};
    r.people.forEach(pp => {
      const g = byGroup[pp.group] || (byGroup[pp.group] = { hours: 0, cost: 0 });
      g.hours += pp.hours; g.cost += pp.cost;
    });
    const margins = {}, revenues = {};
    groups.forEach(g => {
      const gg = byGroup[g];
      if (!gg) { margins[g] = null; revenues[g] = null; return; }
      const rev = r.hours > 0 ? gg.hours / r.hours * r.revenue : 0;   // revenu pro-rata du groupe
      revenues[g] = rev;
      margins[g] = rev - gg.cost;
    });
    return { id: r.id, name: r.name, segment: r.segment, margins, revenues, total: r.margin };
  });
  const bySeg = {};
  svcRows.forEach(r => { (bySeg[r.segment] = bySeg[r.segment] || []).push(r); });
  const segments = Object.keys(bySeg);
  const colTotal = (g) => svcRows.reduce((s, r) => s + (r.margins[g] || 0), 0);
  const colRev = (g) => svcRows.reduce((s, r) => s + (r.revenues[g] || 0), 0);
  const grandTotal = svcRows.reduce((s, r) => s + (r.total || 0), 0);
  const rev = calc.totalRev || 0;
  const pctCell = (mg, rv) => rv > 0 ? `${num(mg / rv * 100, 0)}%` : '—';

  const baseToggle = (
    <SegmentedToggle
      label={p('Base taux horaire')} value={costMode} onChange={setCostMode}
      ariaLabel={t('phrases:p_d6090ea52c')}
      options={[
        { value: 'lcr', label: 'LCR' },
        { value: 'ccr', label: 'CCR' },
        { value: 'pcr', label: 'PCR' },
      ]}
    />
  );

  return (
    /* Aucun filtre sur cet écran : la toolbar ne s'affiche pas, et l'ordre des
       autres zones ne change pas pour autant. */
    <CardShell flush
      title={<>{p('Marge par groupe')} <small className="rc-count">· {year}</small></>}
      subtitle={t('phrases:p_fd37835abe')}
      viewControls={baseToggle}>
      <div className="rc-scroll">
        <table className="rc-table">
          <thead>
            <tr>
              <th className="rc-th-res">{p('Service')}</th>
              {groups.map(g => <th key={g} className="rc-th-num rc-th-prod">{g === 'Sans groupe' ? p('Sans groupe') : g}</th>)}
              <th className="rc-th-num rc-col-res">{p('Total')}</th>
            </tr>
          </thead>
          <tbody>
            {svcRows.length === 0 && <tr><td colSpan={groups.length + 2} className="page-table-empty">{p('Aucun service.')}</td></tr>}
            {segments.map(seg => {
              const list = bySeg[seg];
              const multi = segments.length > 1;
              const segTotalG = (g) => list.reduce((s, r) => s + (r.margins[g] || 0), 0);
              const segTotal = list.reduce((s, r) => s + (r.total || 0), 0);
              return (
                <React.Fragment key={seg}>
                  {list.map(r => (
                    <tr key={r.id}>
                      <td className="rc-res"><span className="rc-res-name">{r.name}</span></td>
                      {groups.map(g => (
                        <td key={g} className="rc-num" style={r.margins[g] != null ? { color: marginColor(r.margins[g]) } : { color: 'var(--color-text-muted)' }}>
                          {r.margins[g] != null ? m(r.margins[g]) : '—'}
                        </td>
                      ))}
                      <td className="rc-num rc-col-res" style={{ color: marginColor(r.total), fontWeight: 700, background: marginTint(r.total) }}>{m(r.total)}</td>
                    </tr>
                  ))}
                  {multi && (
                    <tr className="rc-subtotal">
                      <td className="rc-res">Σ {seg}</td>
                      {groups.map(g => <td key={g} className="rc-num" style={{ color: marginColor(segTotalG(g)), fontWeight: 700 }}>{m(segTotalG(g))}</td>)}
                      <td className="rc-num rc-col-res" style={{ color: marginColor(segTotal), fontWeight: 700 }}>{m(segTotal)}</td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
          <tfoot>
            {/* Marge PAR GROUPE (là où on alloue) + revenus + marge %. */}
            <tr className="rc-foot">
              <td className="rc-res">{costMode === 'lcr' ? p('Marge s/ coûts variables') : costMode === 'ccr' ? p('Marge brute') : p('Marge des services')}</td>
              {groups.map(g => <td key={g} className="rc-num" style={{ color: marginColor(colTotal(g)) }}>{m(colTotal(g))}</td>)}
              <td className="rc-num rc-col-res" style={{ color: marginColor(grandTotal) }}>{m(grandTotal)}</td>
            </tr>
            <tr className="rc-subtotal">
              <td className="rc-res">{p('Revenus')}</td>
              {groups.map(g => <td key={g} className="rc-num">{colRev(g) > 0 ? m(colRev(g)) : '—'}</td>)}
              <td className="rc-num rc-col-res">{m(rev)}</td>
            </tr>
            <tr className="rc-subtotal">
              <td className="rc-res">{p('Marge %')}</td>
              {groups.map(g => { const cr = colRev(g); return <td key={g} className="rc-num" style={{ color: marginColor(colTotal(g)) }}>{cr > 0 ? pctCell(colTotal(g), cr) : '—'}</td>; })}
              <td className="rc-num rc-col-res" style={{ color: marginColor(grandTotal) }}>{pctCell(grandTotal, rev)}</td>
            </tr>
            {/* Coûts NON alloués aux groupes → uniquement en Total (réco vers le résultat net).
                LCR/CCR seulement (PCR alloue tout). Mêmes lignes que l'Analyse. */}
            {costMode === 'lcr' && calc.indirectUnalloc > 0.5 && (
              <tr className="rc-interne">
                <td className="rc-res">{p('Indirect non affecté')}</td>
                {groups.map(g => <td key={g} className="rc-num">—</td>)}
                <td className="rc-num rc-col-res" style={{ color: 'var(--color-terracotta)' }}>{m(-calc.indirectUnalloc)}</td>
              </tr>
            )}
            {costMode !== 'pcr' && calc.idleCost > 0.5 && (
              <tr className="rc-interne">
                <td className="rc-res">{p('Sous-activité')}</td>
                {groups.map(g => <td key={g} className="rc-num">—</td>)}
                <td className="rc-num rc-col-res" style={{ color: 'var(--color-terracotta)' }}>{m(-calc.idleCost)}</td>
              </tr>
            )}
            <tr className="rc-foot">
              <td className="rc-res">{p('RÉSULTAT NET')}</td>
              {groups.map(g => <td key={g} className="rc-num">—</td>)}
              <td className="rc-num rc-col-res" style={{ color: marginColor(calc.netResult) }}>{m(calc.netResult)}{rev > 0 ? <small className="rc-kpi-sub"> · {num(calc.netResult / rev * 100, 0)}%</small> : null}</td>
            </tr>
          </tfoot>
        </table>
      </div>
    </CardShell>
  );
}

function Kpi({ label, value, sub, color, strong }) {
  return (
    <div className={`rc-kpi${strong ? ' rc-kpi--strong' : ''}`}>
      <span className="rc-kpi-label">{label}</span>
      <span className="rc-kpi-value" style={color ? { color } : undefined}>
        {value}{sub ? <small className="rc-kpi-sub"> · {sub}</small> : null}
      </span>
    </div>
  );
}
